import {Button} from "@zendeskgarden/react-buttons";
import {ReactElement} from "react";
import {PayrollCompensationItemModel, PayrollCompensationItemType} from "../../../__generated__/generated_types";
import styles from "../AnalystPayrollDrawer.module.scss";
import BenefitEntry from "../components/BenefitEntry";
import BenefitEntryWithCompensationCap from "../components/BenefitEntryWithCompensationCap";
import {
    LocalPayrollEmployeeCompItemModel,
    LocalPayrollEmployeeModel,
    LocalPayrollEmployeeModelKeys,
} from "../helpers/types";
import {getDefaultLocalPayrollEmployeeCompItemModel} from "../helpers/utils";

interface BenefitsProps {
    employeeData: LocalPayrollEmployeeModel;
    compensationItems:  Pick<PayrollCompensationItemModel, "id" | "type" | "customName">[];
    updateFormState: (key: LocalPayrollEmployeeModelKeys, value: any) => void;
}

export default function TabBenefits({employeeData, compensationItems, updateFormState}: BenefitsProps): ReactElement {
    const employeeLocked = employeeData.propertyLocked === true;

    const allCompItemsWithoutCompensationCap = [{
        label: "Medical",
        items: employeeData.compItems[PayrollCompensationItemType.Medical],
        updateKey: LocalPayrollEmployeeModelKeys.medicalCompItems,
        compensationItem: compensationItems.filter(ci => ci.type === PayrollCompensationItemType.Medical).firstElement
    }, {
        label: "Dental",
        items: employeeData.compItems[PayrollCompensationItemType.Dental],
        updateKey: LocalPayrollEmployeeModelKeys.dentalCompItems,
        compensationItem: compensationItems.filter(ci => ci.type === PayrollCompensationItemType.Dental).firstElement
    }, {
        label: "Vision",
        items: employeeData.compItems[PayrollCompensationItemType.Vision],
        updateKey: LocalPayrollEmployeeModelKeys.visionCompItems,
        compensationItem: compensationItems.filter(ci => ci.type === PayrollCompensationItemType.Vision).firstElement
    }];

    const allCompItemsWithCompensationCap = [{
        label: "401K",
        items: employeeData.compItems[PayrollCompensationItemType.Retirement_401KMatch],
        updateKey: LocalPayrollEmployeeModelKeys.retirement401KCompItems,
        compensationItem: compensationItems.filter(ci => ci.type === PayrollCompensationItemType.Retirement_401KMatch).firstElement
    }];

    return (
        <div className={styles.benefitsContainer}>
            {
                allCompItemsWithoutCompensationCap.map(currentCompItem => {
                    return (<div className={styles.benefitsSections} key={currentCompItem.label}>
                        <div style={{width: "400px"}}>
                            <div>
                                <h4 className={styles.benefitsHeaders}>{currentCompItem.label}</h4>
                            </div>
                            {
                                currentCompItem.items?.map((compItem, index) => {
                                    return <BenefitEntry
                                        key={index}
                                        index={index}
                                        compItem={compItem}
                                        allowRemove={currentCompItem.items.length > 0}
                                        employeeData={employeeData}
                                        updateFormState={(key: LocalPayrollEmployeeModelKeys, value: any) => {
                                            const newCompItems: Array<LocalPayrollEmployeeCompItemModel> =
                                                JSON.parse(JSON.stringify(currentCompItem.items));
                                            const correspondingNewEntry: LocalPayrollEmployeeCompItemModel | undefined = newCompItems[index];
                                            if (correspondingNewEntry) {
                                                switch (key) {
                                                    case LocalPayrollEmployeeModelKeys.compItemsAmount:
                                                        correspondingNewEntry.costPerPayPeriod = value;
                                                        break;
                                                    case LocalPayrollEmployeeModelKeys.compItemsEffectiveDate:
                                                        correspondingNewEntry.effectiveStartDate = value;
                                                        break;
                                                    case LocalPayrollEmployeeModelKeys.removeCompItem:
                                                        newCompItems.splice(index, 1);
                                                        break;
                                                    default:
                                                        console.info(`Unhandled key: ${key} with value: ${JSON.stringify(value)}`);
                                                }
                                            }
                                            updateFormState(currentCompItem.updateKey, newCompItems);
                                        }}
                                    />;
                                })
                            }
                            {!employeeLocked &&
                                <Button isBasic className={styles.addButton} onClick={() => {
                                    let newCompItems: Array<LocalPayrollEmployeeCompItemModel> =
                                        currentCompItem.items ? [...currentCompItem.items] : [];
                                    if(currentCompItem.compensationItem) {
                                        const defaultItem = getDefaultLocalPayrollEmployeeCompItemModel(currentCompItem.compensationItem);
                                        if(defaultItem) {
                                            newCompItems.push(defaultItem);
                                        }
                                    }
                                    updateFormState(currentCompItem.updateKey, newCompItems);
                                }}>Add</Button>
                            }
                        </div>
                    </div>);
                })
            }
            {
                allCompItemsWithCompensationCap.map(currentCompItem => {
                    return (<div className={styles.benefitsSections} key={currentCompItem.label}>
                        <div style={{width: "400px"}}>
                            <div>
                                <h4 className={styles.benefitsHeaders}>{currentCompItem.label}</h4>
                            </div>
                            {
                                currentCompItem.items?.map((compItem, index) => {
                                    return <BenefitEntryWithCompensationCap
                                        key={index}
                                        index={index}
                                        subHeaderLabel={"Contribution"}
                                        compItem={compItem}
                                        employeeData={employeeData}
                                        allowRemove={currentCompItem.items.length > 0}
                                        updateFormState={(key: LocalPayrollEmployeeModelKeys, value: any) => {
                                            const newCompItems: Array<LocalPayrollEmployeeCompItemModel> =
                                                JSON.parse(JSON.stringify(currentCompItem.items));
                                            const correspondingNewEntry: LocalPayrollEmployeeCompItemModel | undefined = newCompItems[index];
                                            if (correspondingNewEntry) {
                                                switch (key) {
                                                    case LocalPayrollEmployeeModelKeys.compItemsAmount:
                                                        correspondingNewEntry.costPerPayPeriod = value;
                                                        break;
                                                    case LocalPayrollEmployeeModelKeys.compItemsCompensationCap:
                                                        correspondingNewEntry.annualCostCap = value;
                                                        break;
                                                    case LocalPayrollEmployeeModelKeys.compItemsEffectiveDate:
                                                        correspondingNewEntry.effectiveStartDate = value;
                                                        break;
                                                    case LocalPayrollEmployeeModelKeys.removeCompItem:
                                                        newCompItems.splice(index, 1);
                                                        break;
                                                    default:
                                                        console.info(`Unhandled key: ${key} with value: ${JSON.stringify(value)}`);
                                                }
                                            }
                                            updateFormState(currentCompItem.updateKey, newCompItems);
                                        }}
                                    />;
                                })
                            }
                            {!employeeLocked &&
                                <Button isBasic className={styles.addButton} onClick={() => {
                                    let newCompItems: Array<LocalPayrollEmployeeCompItemModel> =
                                        currentCompItem.items ? [...currentCompItem.items] : [];
                                    if(currentCompItem.compensationItem) {
                                        const defaultItem = getDefaultLocalPayrollEmployeeCompItemModel(currentCompItem.compensationItem);
                                        if(defaultItem) {
                                            newCompItems.push(defaultItem);
                                        }
                                    }
                                    updateFormState(currentCompItem.updateKey, newCompItems);
                                }}>Add</Button>
                            }
                        </div>
                    </div>);
                })
            }
        </div>
    );
}

